/**
 * Canvas 2D Frame Renderer
 * Draws realistic frame profiles with gradients, bevels, and texture effects.
 * Replaces CSS border-based rendering for professional-quality visuals.
 */

import { getFrameData } from '../../../data/frames';
import { LRUCache } from '../../../lib/lru-cache';

export interface FrameRenderOptions {
  /** Frame template ID */
  templateId: string;
  /** Frame width in pixels (already scaled) */
  frameWidth: number;
  /** Inner width (artwork + mat area) in pixels */
  innerWidth: number;
  /** Inner height (artwork + mat area) in pixels */
  innerHeight: number;
  /** Highlight intensity override (0-1) */
  highlightIntensity?: number;
  /** Shadow intensity override (0-1) */
  shadowIntensity?: number;
  /** Color adjustments */
  colorAdjustments?: {
    hue?: number;
    saturation?: number;
    lightness?: number;
  };
}

/** Cache key for rendered frames */
function cacheKey(opts: FrameRenderOptions): string {
  return `${opts.templateId}_${opts.frameWidth}_${opts.innerWidth}_${opts.innerHeight}_${opts.highlightIntensity}_${opts.shadowIntensity}`;
}

/** Frame render cache (LRU — frequently used frames stay cached) */
const frameCache = new LRUCache<string, HTMLCanvasElement>(20);

/** Parse hex color to RGB */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const num = parseInt(hex.slice(1), 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

/** Lighten a color */
function lighten(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const lr = Math.min(255, Math.round(r + (255 - r) * amount));
  const lg = Math.min(255, Math.round(g + (255 - g) * amount));
  const lb = Math.min(255, Math.round(b + (255 - b) * amount));
  return `rgb(${lr},${lg},${lb})`;
}

/** Darken a color */
function darken(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const dr = Math.round(r * (1 - amount));
  const dg = Math.round(g * (1 - amount));
  const db = Math.round(b * (1 - amount));
  return `rgb(${dr},${dg},${db})`;
}

/**
 * Draw a wood grain texture pattern
 */
function drawWoodGrain(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  _baseColor: string,
  secondaryColor: string,
  opacity: number,
): void {
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.globalCompositeOperation = 'overlay';

  const { r, g, b } = hexToRgb(secondaryColor);

  // Draw grain lines along the longer axis
  const isVertical = height > width;
  const lineCount = isVertical
    ? Math.max(8, Math.round(width / 3))
    : Math.max(8, Math.round(height / 3));

  for (let i = 0; i < lineCount; i++) {
    const t = i / lineCount;
    // Slight wave pattern for natural look
    const waveAmplitude = isVertical ? width * 0.02 : height * 0.02;

    ctx.beginPath();
    ctx.strokeStyle = `rgba(${r},${g},${b},${0.1 + Math.random() * 0.15})`;
    ctx.lineWidth = 0.5 + Math.random() * 1.5;

    if (isVertical) {
      const xPos = x + t * width;
      ctx.moveTo(xPos, y);
      for (let j = 0; j <= height; j += 10) {
        const wave = Math.sin(j * 0.02 + t * 10) * waveAmplitude;
        ctx.lineTo(xPos + wave, y + j);
      }
    } else {
      const yPos = y + t * height;
      ctx.moveTo(x, yPos);
      for (let j = 0; j <= width; j += 10) {
        const wave = Math.sin(j * 0.02 + t * 10) * waveAmplitude;
        ctx.lineTo(x + j, yPos + wave);
      }
    }
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draw brushed metal texture
 */
function drawBrushedMetal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  opacity: number,
): void {
  ctx.save();
  ctx.globalAlpha = opacity * 0.3;
  ctx.globalCompositeOperation = 'overlay';

  // Fine horizontal lines for brushed metal effect
  const lineCount = Math.round(height / 1.5);
  for (let i = 0; i < lineCount; i++) {
    const yPos = y + (i / lineCount) * height;
    const brightness = 128 + Math.random() * 40 - 20;
    ctx.strokeStyle = `rgba(${brightness},${brightness},${brightness},0.3)`;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x, yPos);
    ctx.lineTo(x + width, yPos);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draw a single frame side with gradient bevel
 */
function drawFrameSide(
  ctx: CanvasRenderingContext2D,
  points: [number, number][],
  gradient: CanvasGradient,
): void {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();
}

/**
 * Render a frame to an offscreen canvas.
 * Returns a canvas element that can be drawn onto the main canvas.
 */
export function renderFrame(opts: FrameRenderOptions): HTMLCanvasElement {
  const key = cacheKey(opts);
  const cached = frameCache.get(key);
  if (cached) return cached;

  const frameData = getFrameData(opts.templateId);
  const color = frameData?.color ?? '#1a1a1a';
  const texture = frameData?.texture ?? 'smooth';
  const secondaryColor = frameData?.secondaryColor ?? darken(color, 0.2);
  const profile = frameData?.profile ?? 'flat';

  const fw = opts.frameWidth;
  const iw = opts.innerWidth;
  const ih = opts.innerHeight;
  const totalWidth = iw + fw * 2;
  const totalHeight = ih + fw * 2;

  const canvas = document.createElement('canvas');
  canvas.width = totalWidth;
  canvas.height = totalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Fallback: return a plain-colored canvas
    const fallback = document.createElement('canvas');
    fallback.width = totalWidth;
    fallback.height = totalHeight;
    frameCache.set(key, fallback);
    return fallback;
  }

  const hi = opts.highlightIntensity ?? 0.3;
  const si = opts.shadowIntensity ?? 0.4;

  // Outer corners
  const ox = 0,
    oy = 0;
  const or = totalWidth,
    ob = totalHeight;
  // Inner corners
  const ix = fw,
    iy = fw;
  const ir = fw + iw,
    ib = fw + ih;

  // --- Draw the four frame sides ---

  // Top side: light gradient (light hits from top-left)
  const topGrad = ctx.createLinearGradient(ox, oy, ox, iy);
  topGrad.addColorStop(0, lighten(color, hi * 0.6));
  topGrad.addColorStop(0.3, lighten(color, hi * 0.3));
  topGrad.addColorStop(0.7, color);
  topGrad.addColorStop(1, darken(color, si * 0.15));
  drawFrameSide(
    ctx,
    [
      [ox, oy],
      [or, oy],
      [ir, iy],
      [ix, iy],
    ],
    topGrad,
  );

  // Bottom side: shadow gradient
  const bottomGrad = ctx.createLinearGradient(ox, ib, ox, ob);
  bottomGrad.addColorStop(0, darken(color, si * 0.1));
  bottomGrad.addColorStop(0.3, color);
  bottomGrad.addColorStop(0.7, darken(color, si * 0.3));
  bottomGrad.addColorStop(1, darken(color, si * 0.5));
  drawFrameSide(
    ctx,
    [
      [ox, ob],
      [or, ob],
      [ir, ib],
      [ix, ib],
    ],
    bottomGrad,
  );

  // Left side: lighter gradient
  const leftGrad = ctx.createLinearGradient(ox, oy, ix, oy);
  leftGrad.addColorStop(0, lighten(color, hi * 0.5));
  leftGrad.addColorStop(0.3, lighten(color, hi * 0.2));
  leftGrad.addColorStop(0.7, color);
  leftGrad.addColorStop(1, darken(color, si * 0.1));
  drawFrameSide(
    ctx,
    [
      [ox, oy],
      [ox, ob],
      [ix, ib],
      [ix, iy],
    ],
    leftGrad,
  );

  // Right side: darker gradient
  const rightGrad = ctx.createLinearGradient(ir, oy, or, oy);
  rightGrad.addColorStop(0, darken(color, si * 0.1));
  rightGrad.addColorStop(0.3, color);
  rightGrad.addColorStop(0.7, darken(color, si * 0.3));
  rightGrad.addColorStop(1, darken(color, si * 0.4));
  drawFrameSide(
    ctx,
    [
      [or, oy],
      [or, ob],
      [ir, ib],
      [ir, iy],
    ],
    rightGrad,
  );

  // --- Inner bevel (lip where frame meets mat/artwork) ---
  if (profile !== 'floater') {
    const bevelWidth = Math.max(2, fw * 0.08);

    // Inner highlight (top/left of opening)
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = hi * 0.4;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(ix, iy, iw, bevelWidth); // top
    ctx.fillRect(ix, iy, bevelWidth, ih); // left
    ctx.restore();

    // Inner shadow (bottom/right of opening)
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = si * 0.3;
    ctx.fillStyle = '#000000';
    ctx.fillRect(ix, ib - bevelWidth, iw, bevelWidth); // bottom
    ctx.fillRect(ir - bevelWidth, iy, bevelWidth, ih); // right
    ctx.restore();
  }

  // --- Stepped profile second layer ---
  if (profile === 'stepped' && fw > 15) {
    const stepOffset = fw * 0.4;
    const stepWidth = fw * 0.15;

    ctx.save();
    ctx.globalAlpha = 0.15;

    // Top step line
    ctx.fillStyle = lighten(color, 0.3);
    ctx.fillRect(stepOffset, stepOffset, totalWidth - stepOffset * 2, stepWidth);

    // Bottom step line
    ctx.fillStyle = darken(color, 0.2);
    ctx.fillRect(
      stepOffset,
      totalHeight - stepOffset - stepWidth,
      totalWidth - stepOffset * 2,
      stepWidth,
    );

    // Left step line
    ctx.fillStyle = lighten(color, 0.2);
    ctx.fillRect(stepOffset, stepOffset, stepWidth, totalHeight - stepOffset * 2);

    // Right step line
    ctx.fillStyle = darken(color, 0.15);
    ctx.fillRect(
      totalWidth - stepOffset - stepWidth,
      stepOffset,
      stepWidth,
      totalHeight - stepOffset * 2,
    );

    ctx.restore();
  }

  // --- Ornate profile decorative elements ---
  if (profile === 'ornate' && fw > 20) {
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.15;

    // Outer ornate ridge
    const ridgeOffset = fw * 0.15;
    const ridgeWidth = fw * 0.08;

    ctx.fillStyle = lighten(color, 0.4);
    ctx.fillRect(ridgeOffset, ridgeOffset, totalWidth - ridgeOffset * 2, ridgeWidth);
    ctx.fillRect(ridgeOffset, ridgeOffset, ridgeWidth, totalHeight - ridgeOffset * 2);

    ctx.fillStyle = darken(color, 0.3);
    ctx.fillRect(
      ridgeOffset,
      totalHeight - ridgeOffset - ridgeWidth,
      totalWidth - ridgeOffset * 2,
      ridgeWidth,
    );
    ctx.fillRect(
      totalWidth - ridgeOffset - ridgeWidth,
      ridgeOffset,
      ridgeWidth,
      totalHeight - ridgeOffset * 2,
    );

    // Inner ornate ridge
    const innerRidgeOffset = fw * 0.7;
    ctx.fillStyle = lighten(color, 0.3);
    ctx.fillRect(innerRidgeOffset, innerRidgeOffset, totalWidth - innerRidgeOffset * 2, ridgeWidth);
    ctx.fillRect(
      innerRidgeOffset,
      innerRidgeOffset,
      ridgeWidth,
      totalHeight - innerRidgeOffset * 2,
    );

    ctx.fillStyle = darken(color, 0.25);
    ctx.fillRect(
      innerRidgeOffset,
      totalHeight - innerRidgeOffset - ridgeWidth,
      totalWidth - innerRidgeOffset * 2,
      ridgeWidth,
    );
    ctx.fillRect(
      totalWidth - innerRidgeOffset - ridgeWidth,
      innerRidgeOffset,
      ridgeWidth,
      totalHeight - innerRidgeOffset * 2,
    );

    ctx.restore();
  }

  // --- Texture overlays ---
  if (texture === 'wood-grain') {
    // Top
    drawWoodGrain(ctx, ox, oy, totalWidth, fw, color, secondaryColor, 0.4);
    // Bottom
    drawWoodGrain(ctx, ox, ib, totalWidth, fw, color, secondaryColor, 0.4);
    // Left
    drawWoodGrain(ctx, ox, oy, fw, totalHeight, color, secondaryColor, 0.4);
    // Right
    drawWoodGrain(ctx, ir, oy, fw, totalHeight, color, secondaryColor, 0.4);
  } else if (texture === 'brushed-metal') {
    // Top
    drawBrushedMetal(ctx, ox, oy, totalWidth, fw, 0.5);
    // Bottom
    drawBrushedMetal(ctx, ox, ib, totalWidth, fw, 0.5);
    // Left - rotated effect via drawing horizontal anyway
    drawBrushedMetal(ctx, ox, oy, fw, totalHeight, 0.5);
    // Right
    drawBrushedMetal(ctx, ir, oy, fw, totalHeight, 0.5);
  } else if (texture === 'gilded') {
    // Golden sheen - bright highlight streak
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.2;

    const sheenGrad = ctx.createLinearGradient(0, 0, totalWidth, totalHeight);
    sheenGrad.addColorStop(0, 'rgba(255,255,255,0)');
    sheenGrad.addColorStop(0.3, 'rgba(255,255,200,0.4)');
    sheenGrad.addColorStop(0.5, 'rgba(255,255,255,0.6)');
    sheenGrad.addColorStop(0.7, 'rgba(255,255,200,0.4)');
    sheenGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheenGrad;
    ctx.fillRect(ox, oy, totalWidth, totalHeight);

    ctx.restore();
  } else if (texture === 'distressed') {
    // Random scratches and wear marks
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.1;

    for (let i = 0; i < 30; i++) {
      const sx = Math.random() * totalWidth;
      const sy = Math.random() * totalHeight;
      const sw = 1 + Math.random() * 3;
      const sh = 1 + Math.random() * 8;
      ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
      ctx.fillRect(sx, sy, sw, sh);
    }

    ctx.restore();
  }

  // --- Floater gap effect ---
  if (profile === 'floater') {
    // Draw a thin gap between frame and artwork
    const gapWidth = Math.max(2, fw * 0.2);
    ctx.save();
    ctx.fillStyle = '#1a1a1a'; // Dark gap
    ctx.fillRect(ix, iy, iw, gapWidth); // top gap
    ctx.fillRect(ix, ib - gapWidth, iw, gapWidth); // bottom gap
    ctx.fillRect(ix, iy, gapWidth, ih); // left gap
    ctx.fillRect(ir - gapWidth, iy, gapWidth, ih); // right gap
    ctx.restore();
  }

  // --- Outer edge shadow ---
  ctx.save();
  ctx.globalAlpha = 0.08;
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, totalWidth - 1, totalHeight - 1);
  ctx.restore();

  // Cache the result (LRU handles eviction)
  frameCache.set(key, canvas);

  return canvas;
}

/** Clear the frame render cache */
export function clearFrameCache(): void {
  frameCache.clear();
}
